import * as React from "react";
import * as moment from "moment";
import {
    AnnualPolicyInfo,
    BreakdownCoverLevelEnum,
    CAR_WOLF_RENEWAL_NCD_PROOF,
    CommonHelpers,
    Document,
    ICustomerPortalService,
    InstalmentsInfo,
    INTERACTION_PROCESS_MTA_NB_CHANGE_START_DATE,
    INTERACTION_PROCESS_NB,
    INTERACTION_PROCESS_RENEWAL,
    InteractionHelper,
    MOTOR_COVER_TIERS,
    Policy,
    POLICY_STATUS_MTC,
    POLICY_STATUS_PENDING_RENEWAL,
    PREMIUM_TYPE_ANNUAL,
    productHelper,
    Renewal,
    resourcesHelper,
    Subscription,
    Types
} from "@falcon/services";
import {resolveClaimType, resolveFuelType, resolveTransmission} from "../../../../../utils/resolveLookupValues";
import {
    formatDate,
    formatDateTime,
    getAddressLines,
    getCursorClassName,
    getGuaranteedHireCarText,
    getIsOptionalExtraIncludedText,
    getLink
} from "../../../../../CommonFunctions";
import {Link} from "react-router-dom";
import {lazyInject} from "@athena-portal/core";
import {
    DocumentListModal,
    getInteractionVehicleFuelType,
    PolicySummaryPriceWidget
} from "@falcon/plugin-falcon-journey";
import {Modal, ModalBody, ModalHeader} from "reactstrap";
import {ButtonLink} from "../../../../../components/navigation/ButtonLink";
import {Types as JourneyCoreTypeDefs, ILookupService} from "@athena-portal/plugin-journey";
import {ListItem} from "@athena-portal/journey-model";

interface IProps {
    policy: Policy;
    // Policy that is still active but is going to be replaced
    currentlyActivePolicy?: Policy;
    subscription: Subscription;
    annualPolicyInfo: AnnualPolicyInfo;
    vehicleIndex: number;
    isOldPolicy?: boolean;
    renewalProposal?: Renewal;
    policyEndDate: string;
}

interface IState {
    documentPathToSizeMaps: Array<Map<string, string>>;
    instalmentsInfo?: InstalmentsInfo;
    showChangedStartDatePopup: boolean;
    ncdDocument: Document | null;
    mtaOnEndDateOfPolicy: boolean;
    lookup: Record<string, ListItem[]>;
}

export class BritanniaYourCarCoverPanel extends React.Component<IProps, IState> {

    private readonly alertMessage: string | undefined = undefined;
    private readonly policyToRender: Policy;
    private readonly vehicleToRender: any;
    private readonly cancelled: boolean;
    private readonly vehicleDrivers: any[] = [];
    private readonly mtaOnEndDateOfPolicy: boolean;

    @lazyInject(Types.CustomerPortalService)
    private readonly customerPortalService: ICustomerPortalService;

    @lazyInject(JourneyCoreTypeDefs.LookupService)
    private readonly lookupService: ILookupService;

    constructor(props: IProps) {
        super(props);

        this.state = {
            lookup: {},
            documentPathToSizeMaps: [],
            showChangedStartDatePopup: false,
            ncdDocument: null,
            mtaOnEndDateOfPolicy: false
        };
        this.toggleHasAlreadyChangedStartDate = this.toggleHasAlreadyChangedStartDate.bind(this);

        this.policyToRender = this.props.currentlyActivePolicy || this.props.policy;

        this.vehicleToRender = this.policyToRender.interaction.vehicles[0];
        this.cancelled = this.props.policy.status === POLICY_STATUS_MTC;

        const allDrivers: any[] = [this.policyToRender.interaction.policyHolder].concat(this.policyToRender.interaction.additionalDrivers || []);
        if (this.vehicleToRender.driverIds) {
            this.vehicleDrivers = this.vehicleToRender.driverIds
                .map((driverId: string) => allDrivers.find((driver: any) => driverId === driver.personId))
                .filter((driver: any) => driver !== undefined);
        }

        if (this.props.annualPolicyInfo.hasDebt) {
            this.alertMessage = "Due to missed payment this action is not allowed";
        } else if (this.props.subscription?.allAnnualMotorPoliciesAreCancelled) {
            this.alertMessage = "Sorry, due to cancellation this action is not allowed.";
        }

        this.mtaOnEndDateOfPolicy = moment.utc(this.props.policy.endDate).isSame(moment.utc(this.props.annualPolicyInfo?.maxFutureMtaDate), 'day');
    }

    public async componentDidMount() {
        const agreementVersionIds: number[] = this.props.policy.policyHistoryList
            .filter(ph => ph.status !== POLICY_STATUS_MTC)
            .map(ph => ph.id);

        this.customerPortalService.getCombinedInstalmentsInfo(agreementVersionIds)
            .subscribe((instalmentsInfo: any) => {
                this.setState({instalmentsInfo});
            });


        this.customerPortalService.getDocumentByNameAndPolicyNumber(this.policyToRender.policyNumber, CAR_WOLF_RENEWAL_NCD_PROOF + ".pdf").subscribe(
            (incomingNcd: Document) => {
                this.setState({ncdDocument: incomingNcd})
            });
        this.fetchLookupValues("carUsage");
        this.fetchLookupValues("mileage");
        this.fetchLookupValues("drivingLicenceTypes");
    }

    public render() {
        const {vehicleIndex} = this.props;
        const interaction: any = this.policyToRender.interaction;
        const coverTier = this.policyToRender.interaction.coverTier;
        const premiumType: string = interaction.factors.premiumType;
        const price: number = premiumType === PREMIUM_TYPE_ANNUAL ? interaction.factors.annualPremium : this.props.policy.interaction.paymentDetails.instalmentPlan.monthlyPayment;
        const totalPaid: number = this.props.policy.interaction.paymentDetails.instalmentPlan.totalAmount;

        return (
            <div className="section-panel your-cover-panel">
                <div className="d-flex justify-content-between align-items-center pb-3">
                    <div>
                        <img className="main-icon cover-tier-icon"
                             src={`/${resourcesHelper.THEME}/img/${this.getCoverTierIcon(coverTier)}`}
                             alt="Cover tier icon"/>
                    </div>
                    <div className="d-flex d-md-none">
                        {this.renderCoverTierHeader(coverTier)}
                    </div>
                </div>
                <div className="d-flex justify-content-md-between justify-content-end align-items-center pb-2 pb-md-3">
                    <div className="d-none d-md-block">
                        {this.renderCoverTierHeader(coverTier)}
                    </div>
                    {!this.cancelled && <PolicySummaryPriceWidget
                        price={price}
                        premiumType={premiumType}
                        totalPaid={totalPaid}
                    />}
                </div>
                <img className="main-icon policy-icon bn-policy-icon" src={`/${resourcesHelper.THEME}/img/policy-summary.png`}
                     alt="Car cover icon"/>
                <div className="section cover-details">
                    <h2 className="text-center mt-3 mb-3 policy-heading margin-top-0">Cover details</h2>
                    <div className="section-contents">
                        {this.renderCoverDetails(vehicleIndex)}
                    </div>
                </div>
                <div className="section car-details">
                    <h2 className="text-center mt-3 mb-3 car-details-heading margin-top-0">Car details</h2>
                    <img className="main-icon car-icon" src={`/${resourcesHelper.THEME}/img/wolf-your-car.png`}
                         alt="Car icon"/>
                    <div className="section-contents">
                        {this.renderCarDetails()}
                    </div>
                </div>
                <h2 className="text-center mt-3 mb-3 drivers-heading margin-top-0 margin-bottom-0">Drivers details</h2>
                <img className="main-icon people-icon" src={`/${resourcesHelper.THEME}/img/account-details.png`}
                     alt="Car icon"/>
                {this.renderDriverDetailSections()}
                <div className="section policy-documents">
                    <div className="section-heading documents-heading margin-bottom-0">Documents</div>
                    <DocumentListModal
                        productName={productHelper.parseProductName(this.policyToRender.products[0])}
                        coverTier={coverTier}
                        policyDate={InteractionHelper.getPolicyDateForDocumentsFromPolicy(this.policyToRender.interaction)}
                        isBDIVisible={this.policyToRender.interaction.optionalExtras.breakdownCover}
                        isMLEVisible={this.policyToRender.interaction.optionalExtras.motorLegalExpenses}
                        isAccordion={false}
                    />
                </div>
                {!this.props.isOldPolicy && (
                    <div className="section button-section">
                        <div className="section-links">
                            {this.renderCancelPolicyButton(vehicleIndex)}
                        </div>
                        <div className="section-links">
                            <Link to={`/dashboard/pickAnnualMotorClaimKind/${this.props.policy.policyNumber}`}
                                  id={`makeClaim${vehicleIndex}`}
                                  className={`buttonLink`}>
                                <button type="button"
                                        className={`btn btn-primary btn-next plugin`}>
                                    Make a claim
                                </button>
                            </Link>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    private renderCoverDetails(vehicleIndex: number): React.ReactNode {
        const interaction: any = this.policyToRender.interaction;
        const premiumType: string = interaction.factors.premiumType;
        const closeBtn = <button className="btn-close" onClick={this.toggleHasAlreadyChangedStartDate}/>
        const process: string = interaction.process;
        const mtaNotPresent: boolean = process === INTERACTION_PROCESS_NB || process === INTERACTION_PROCESS_RENEWAL;
        const showChangeStartDateLink: boolean = this.shouldShowChangeStartDateLink();
        return (
            <React.Fragment>
                <div className="detail cover-start-date">
                    <div className="detail-key">Cover start date:</div>
                    <div className="detail-value"
                         id={`vehicle${vehicleIndex}-startDate`}>{this.renderPolicyStartDate(this.policyToRender)}</div>
                    {showChangeStartDateLink && (interaction.process === INTERACTION_PROCESS_NB || interaction.process === INTERACTION_PROCESS_MTA_NB_CHANGE_START_DATE)
                        && moment().utc().isBefore(moment(interaction.headerInfo.startDate).utc()) &&
                        <div className="detail-value">
                            <Link
                                className="text-decoration-underline cursor-pointer text-blue-1 muli-bold-16"
                                to={getLink(`/journey/show/product/PropositionWolfMotorInsurance/process/mtaNbChangeStartDate/presetPage/${this.props.policy.interaction._id}`, interaction.hasChangedStartDate)}
                                onClick={this.toggleHasAlreadyChangedStartDate} id="changeStartDate">
                                Change start date
                            </Link>
                        </div>}
                </div>
                {interaction.hasChangedStartDate &&
                    <Modal className="falcon-modal" isOpen={this.state.showChangedStartDatePopup}
                           toggle={this.toggleHasAlreadyChangedStartDate}>
                        <ModalHeader toggle={this.toggleHasAlreadyChangedStartDate} close={closeBtn}/>
                        <ModalBody className="pt-0 overflow-auto">
                            <img src={`/${resourcesHelper.THEME}/img/alert-blue.svg`} alt="Blue alert icon"/>
                            <p className="d-inline mt-0 mb-2 ml-3 mr-3 font-weight-bold fs-24">Change of the policy
                                start date</p>
                            <p className="pt-3 mb-1">You have already changed your policy start date. Changing the start
                                date is only allowed once.
                                If the start date is still wrong, you'll need to cancel this policy and set up a new
                                one.</p>
                        </ModalBody>
                    </Modal>
                }
                <div className="detail">
                    <div className="detail-key">Cover end date:</div>
                    <div className="detail-value"
                         id={`vehicle${vehicleIndex}-endDate`}>{this.props.isOldPolicy ? `${formatDateTime(this.policyToRender.endDate)}`
                        : `${moment.utc(this.policyToRender.endDate, moment.ISO_8601).format("DD MMM YYYY")}, 23:59`}
                    </div>
                </div>
                {this.getDetail("Cover type:", "Comprehensive")}
                {InteractionHelper.isPolicyQuotedBeforeFamilyCoverRemoval(interaction) &&
                    this.getDetail("Family Package:", interaction.optionalExtras.familyPackage ? "Included" : "Not included")
                }
                {this.getDetail("Breakdown cover:", this.getBreakdownWording(interaction.optionalExtras))}
                {this.getDetail("Protected no claims discount:", interaction.policyHolder.ncd.doNotDisclose ? "Included" : "Not included")}
                {this.getDetail("Motor legal expenses:", getIsOptionalExtraIncludedText(interaction.coverTier, interaction.optionalExtras.motorLegalExpenses))}
                {this.getDetail(this.getGHCIncludeDescription(interaction.coverTier), this.getGHCIncludeWording(interaction.coverTier, interaction.optionalExtras))}
                {this.getDetail("Guaranteed hire car:", getGuaranteedHireCarText(interaction.coverTier, interaction.optionalExtras))}

                <div className="detail premium-tax">
                    <div className="detail-value">
                        {premiumType === PREMIUM_TYPE_ANNUAL && mtaNotPresent ? '* includes any changes made during the policy year' : '* includes any changes that you’ve made'}
                    </div>
                    <div className="detail-value">{premiumType === PREMIUM_TYPE_ANNUAL ? 'incl. Insurance Premium Tax' : 'incl. Interest and Insurance Premium Tax'}</div>
                </div>
            </React.Fragment>
        );
    }

    private shouldShowChangeStartDateLink(): boolean {
        return process.env.REACT_APP_CHANGE_START_DATE_ENABLED ? JSON.parse(process.env.REACT_APP_CHANGE_START_DATE_ENABLED) : false;
    }

    private renderCarDetails(): React.ReactNode {
        const vehicleExcesses: any = this.policyToRender.interaction.vehicles[0].annualVehicleExcess;
        const vehicleFuelTypeActual = getInteractionVehicleFuelType(this.vehicleToRender.fuelType, this.vehicleToRender.fuelTypeFull);
        const classOfUseValue = this.vehicleToRender.classOfUse;
        const formatRange = (range: string) => {
            return range
                .split(" - ")
                .map(num => Number(num).toLocaleString("en-GB"))
                .join(" - ");
        };
        const annualMileage = this.resolveLookupValue("mileage", this.vehicleToRender.mileage.toString());
        const carUsage = this.resolveLookupValue("carUsage", classOfUseValue);
        return (
            <React.Fragment>
                <div className="detail car-details-section">
                    <div
                        className="detail-value car-model ">{`${this.vehicleToRender.make} ${this.vehicleToRender.model}`}
                        <span className="number-plate-preview">{`${this.vehicleToRender.registrationNumber}`}</span>
                    </div>
                    <div className="detail-value">{`${this.vehicleToRender.engineSize}CC, ${this.vehicleToRender.doors} dr, ${resolveTransmission(this.vehicleToRender.transmission)}, ${resolveFuelType(vehicleFuelTypeActual)}`}</div>
                    <div className="detail-value">{`Manufactured in ${formatDate(this.vehicleToRender.year, "YYYY")}`}</div>
                    <div className="detail-value">{`Purchased in ${formatDate(this.vehicleToRender.purchaseDate, "MMM YYYY")}`}</div>
                    <div className="detail">
                        <div className="detail-key">Annual mileage:</div>
                        <div className="detail-value">{`${formatRange(annualMileage)}`}</div>
                    </div>
                    <div className="detail">
                        <div className="detail-key">Postcode where the car is kept:</div>
                        <div className="detail-value">{`${this.vehicleToRender.overnightLocationPostcode}`}</div>
                    </div>
                    <div className="detail">
                        <div className="detail-key">Car usage:</div>
                        <div className="detail-value">{carUsage}</div>
                    </div>
                    <div className="text-center">
                        {this.isMtaDisabled() &&
                            <>
                                <button type="button" disabled className="btn btn-gray"
                                        style={{marginRight: "16px"}}>Edit
                                </button>
                                <button type="button" disabled className="btn btn-gray">Replace car</button>
                            </>
                        }
                    </div>
                </div>
                <div className="detail excess-section">
                    <h2 className="text-center mt-3 mb-3 excess-heading margin-bottom-0">Excesses that apply</h2>
                    <div className="detail accidental-damage">
                        <div className={"text-center detail-key"}>Accidental damage:</div>
                        <div
                            className="detail-value price">
                            <span
                                className="price">{`£${vehicleExcesses.compulsoryExcess + vehicleExcesses.voluntaryExcess} `}</span>
                            <span
                                className="voluntary-excess">{`(this includes £${vehicleExcesses.voluntaryExcess} voluntary excess)`}</span>
                        </div>
                        <br/>
                    </div>
                    <div className="detail fire-or-theft">
                        <div className={"text-center detail-key"}>Fire or theft:</div>
                        <div
                            className="detail-value">
                            <span
                                className="price">{`£${vehicleExcesses.compulsoryExcess + vehicleExcesses.voluntaryExcess} `}</span>
                            <span
                                className="voluntary-excess">{`(this includes £${vehicleExcesses.voluntaryExcess} voluntary excess)`}</span>
                        </div>
                        <br/>
                    </div>
                    <div className={"detail non-recommended-excess mt-4 mb-0"}>
                        <div className="font-weight-semi-bold">Non-recommended repairer excess costs you more</div>
                        <div className="detail-key">Non-recommended repairer excess:</div>
                        <div className="detail-value">
                                <span
                                    className="price font-weight-bold">{`£${vehicleExcesses.nonRecommendedRepairerExcess}`}</span>
                        </div>
                    </div>
                    <div className={"detail mt-0 mb-0"}>This is in addition to the excesses shown above if you choose a
                        garage <span
                            className="font-weight-semi-bold">not</span> on our list of recommended repairers.
                    </div>
                </div>
                <div className="detail drivers">
                    <div className="detail-key driver-heading">Drivers</div>
                    {this.renderVehicleDrivers()}
                </div>
                <div className="detail car-details-section">
                    <div className="text-center">
                        {!this.props.isOldPolicy && !this.isMtaDisabled() &&
                            <div className="detail-key link-section button-row d-md-inline-block d-flex">
                                <ButtonLink
                                    id={`editCar1Details`}
                                    aria-label="Edit car details"
                                    primaryNoArrow={true}
                                    className="buttonLink--mr"
                                    to={this.isDisabled() ? "" : getLink(`/journey/init/product/AnnualMotorInsurance/process/mtaEditVehicleDetails/editCarEntryPage/${this.props.policy.interaction._id}`, this.hasDebt())}
                                    disabled={this.isDisabled()}
                                    alertMessage={this.alertMessage}
                                >Edit car details</ButtonLink>
                                <ButtonLink
                                    id={`replaceCar1`}
                                    aria-label="Replace car"
                                    primary={false}
                                    to={this.isDisabled() ? "" : getLink(`/journey/init/product/AnnualMotorInsurance/process/mtaReplaceCar/preset/${this.props.policy.interaction._id}`, this.hasDebt())}
                                    disabled={this.isDisabled()}
                                    alertMessage={this.alertMessage}
                                >Change car</ButtonLink>
                            </div>
                        }

                    </div>
                </div>
            </React.Fragment>
        );
    }

    private toggleHasAlreadyChangedStartDate(): void {
        this.setState((state: IState) => ({
            showChangedStartDatePopup: !state.showChangedStartDatePopup
        }));
    }

    private hasDebt() {
        const {hasDebt, hasUnpaidChargebackInvoice, hasUnpaidChange} = this.props.annualPolicyInfo!;
        return hasDebt || hasUnpaidChargebackInvoice || hasUnpaidChange;
    }

    private renderVehicleDrivers(): React.ReactNode[] {
        return this.vehicleDrivers.map((driver: any) => {
            return (
                <div key={driver.personId} className="detail-value">
                    {`${driver.firstName} ${driver.lastName}`} {driver.personId === this.vehicleToRender.mainDriverId &&
                    <span>(main driver)</span>}
                </div>
            );
        });
    }

    private renderDriverDetailSections(): React.ReactNode[] {
        return this.vehicleDrivers.map((driver: any, index: number) => (
            <div key={driver.personId} className="section driver-details">
                <div className="section-contents">
                    {this.renderDriverDetails(driver, index)}
                </div>
            </div>
        ));
    }

    private isMtaDisabled(): boolean {
        return this.props.annualPolicyInfo?.maxMtaDateReached || this.mtaOnEndDateOfPolicy
            || this.props.subscription?.allAnnualMotorPoliciesAreCancelled;
    }

    private isDisabled() {
        return this.props.annualPolicyInfo.maxMtaDateReached || this.disableLinks();
    }

    private disableLinks(): boolean {
        const {hasDebt, hasUnpaidChargebackInvoice, hasUnpaidChange, maxFutureMtaDate} = this.props.annualPolicyInfo;
        const mtaOnEndDateOfPolicy: boolean = moment.utc(this.props.policyEndDate).isSame(moment.utc(maxFutureMtaDate), 'day');
        if (this.props.subscription) {
            const {allAnnualMotorPoliciesAreCancelled} = this.props.subscription;
            return hasDebt || allAnnualMotorPoliciesAreCancelled || hasUnpaidChargebackInvoice || hasUnpaidChange || mtaOnEndDateOfPolicy;
        } else {
            return hasDebt || hasUnpaidChargebackInvoice || hasUnpaidChange || mtaOnEndDateOfPolicy;
        }
    }

    private renderDriverDetails(driver: any, index: number): React.ReactNode {
        const isPolicyHolder: boolean = driver.personId === this.policyToRender.interaction.policyHolder.personId
        const {vehicleIndex} = this.props;
        const interactionId = this.policyToRender.interaction._id;
        const isHolder = isPolicyHolder ? "PolicyHolder" : index - 1;

        let driverSegment: string;
        if (isPolicyHolder) {
            driverSegment = "main-driver";
        } else {
            driverSegment = `additional-driver-` + index;
        }
        const linkId = `vehicle-${vehicleIndex}-remove-${driverSegment}`;
    return (
            <React.Fragment>
                <div className="detail driver-name">
                    <div className="detail-value">{`${driver.firstName} ${driver.lastName}`}</div>
                </div>
                <div className="detail">
                    <div className="detail-key">Date of birth:</div>
                    <div
                        className="detail-value">{moment(driver.dateOfBirth, moment.ISO_8601).format("D MMMM YYYY")}</div>
                </div>
                <div className="detail">
                    <div className="detail-key">Driving licence type:</div>
                    <div
                        className="detail-value">{this.resolveLookupValue("drivingLicenceTypes", driver.licence.type.toString())}</div>
                </div>
                <div className="detail">
                    <div className="detail-key">Claims in the last 5 years:</div>
                    {this.renderClaims(driver)}
                </div>
                <div className="detail">
                    <div className="detail-key">Criminal convictions:</div>
                    <div
                        className="detail-value">{CommonHelpers.booleanToText(driver.hasUnspentCriminalConvictions, "Yes", "None")}</div>
                </div>
                {this.renderMotoringConvictions(driver)}
                {isPolicyHolder && this.renderPolicyHolderDetails()}
                <div className="text-center detail detail-actions">
                    {!this.props.isOldPolicy && !this.isMtaDisabled() &&
                        <div className="detail-key link-section w-100 w-md-unset d-flex d-md-inline-block">
                            <ButtonLink
                                id={`editDiver${index}`}
                                aria-label="Edit driver details"
                                primaryNoArrow={true}
                                className="buttonLink--mr"
                                to={this.isDisabled() ? "" : getLink(`/journey/init/product/AnnualMotorInsurance/process/mtaEditDriverDetails/presetDriver${isHolder}/${this.props.policy.interaction._id}`, this.hasDebt())}
                                disabled={this.isDisabled()}
                                alertMessage={this.alertMessage}
                            >View / Edit driver</ButtonLink>
                            {!isPolicyHolder &&
                                <ButtonLink
                                    id={linkId}
                                    aria-label="View or edit driver"
                                    primary={false}
                                    to={this.isDisabled() ? "" : getLink(`/journey/show/product/PropositionWolfMotorInsurance/process/mtaRemoveDriver/removeDriver${index - 1}EntryPage/${interactionId}`, this.isMtaDisabled())}
                                    disabled={this.isDisabled()}
                                    alertMessage={this.alertMessage}
                                >Delete driver</ButtonLink>}
                        </div>
                    }
                    {!this.props.isOldPolicy && this.isMtaDisabled() &&
                        <button type="button" disabled className="btn btn-gray">View / Edit driver</button>
                    }
                </div>
            </React.Fragment>
        );
    }

    private renderPolicyHolderDetails(): React.ReactNode {
        const party = this.policyToRender.policyHolder;
        const {policyHolder} = this.policyToRender.interaction;
        const addressComponents: string[] = getAddressLines(policyHolder.address);
        const addressComponentsToRender = addressComponents.map((component, idx) => (
            <span key={component} className="detail-value">
                {idx === 0 ? component : `, ${component}`}
            </span>
        ));
        const hasUnpaidChargebackInvoice: boolean = this.props.annualPolicyInfo!.hasUnpaidChargebackInvoice;

        return (
            <React.Fragment>
                <div className="detail">
                    <div className="detail-key">No claim discount (NCD):</div>
                    <div className="detail-value">{policyHolder.ncd.doNotDisclose ? "Protected" : "Not protected"}</div>
                </div>
                <div className="detail">
                    <div className="detail-key">Years of no claim discount (NCD):</div>
                    <div
                        className="detail-value">{policyHolder.ncd.yearCount > 0 ? policyHolder.ncd.yearCount : "None"}</div>
                </div>
                {policyHolder.ncd.earningType &&
                    <div className="detail">
                        <div className="detail-key">NCD earned:</div>
                        <div
                            className="detail-value">{`${this.ncdEarnTypeWording(policyHolder.ncd.earningType)}`}</div>
                    </div>}
                <div className="detail address">
                    <div className="detail-key">Address:</div>
                    {addressComponentsToRender}
                    <div className="text-center change-address-link">
                        {!this.props.isOldPolicy && (!this.props.annualPolicyInfo?.maxMtaDateReached && !this.mtaOnEndDateOfPolicy) && (
                            <div className="detail-key">
                                <Link
                                    to={getLink('/journey/show/product/PropositionWolfMotorInsurance/process/mtaChangeAddress/preset/' + this.policyToRender.interaction._id, hasUnpaidChargebackInvoice)}
                                    className={`text-decoration-underline cursor-pointer text-blue-1 muli-bold-16 ${getCursorClassName(hasUnpaidChargebackInvoice)}`}
                                    id="editAddress"
                                >Change address</Link>
                            </div>
                        )}
                        {!this.props.isOldPolicy && (this.props.annualPolicyInfo?.maxMtaDateReached || this.mtaOnEndDateOfPolicy) &&
                            <span className="detail-key change-address-link">Change address</span>}
                    </div>
                </div>
                <div className="detail">
                    <div className="detail-key">Email:</div>
                    <br/>
                    <div className="detail-value">
                        {party.email}
                    </div>
                </div>
                <div className="detail">
                    <div className="detail-key">Phone number:</div>
                    <br/>
                    <div className="detail-value">
                        {party.phoneMobile}
                    </div>
                </div>
            </React.Fragment>
        );
    }

    private getBreakdownWording(optionalExtras: any): string {
        if (optionalExtras.breakdownCover === false) {
            return "Not included";
        }
        switch (optionalExtras.breakdownCoverLevel) {
            case BreakdownCoverLevelEnum.LOCAL:
                return "Included (Local)"
            case BreakdownCoverLevelEnum.UK:
                return "Included (National)"
            case BreakdownCoverLevelEnum.EU:
                return "Included (European)"
            default:
                throw new Error(`Unknown Breakdown Cover level: ${optionalExtras.breakdownCoverLevel}`);
        }
    }

    private renderPolicyStartDate(policy: any) {
        if (moment.utc(policy.startDate, moment.ISO_8601).format("HH:mm") === '00:00') {
            const dateText = moment(policy.startDate, moment.ISO_8601).format("DD MMM YYYY");
            const timeText = this.props.isOldPolicy ? "00:00" : "00:01";
            return `${dateText}, ${timeText}`;
        } else {
            return moment(policy.startDate, moment.ISO_8601).format("DD MMM YYYY, HH:mm");
        }
    }

    private renderClaims(driver: any): React.ReactNode | React.ReactNode[] {
        if (driver.hasClaims && driver.numberOfClaims > 0 && driver.claims?.length > 0) {
            return driver.claims.map((claim: any, index: any) =>
                <div key={claim.claimType + index}
                     className="detail-value">{resolveClaimType(claim.claimType)} in {" "}
                    {formatDate(claim.date, "MMM YYYY")}{index !== driver.claims.length - 1 && ", "}</div>
            )
        } else {
            return <div className="detail-value">None</div>
        }
    }

    private renderMotoringConvictions(driver: any): React.ReactNode {
        if (driver?.dlnLookupData?.dlnLookupCallSucess) {
            return (
                <div className="detail">
                    <div className="detail-key">
                        Motoring convictions:
                    </div>
                    <div className="detail-value">
                        As per your DVLA records
                    </div>
                </div>
            );
        } else {
            return (
                <div className="detail">
                    <div className="detail-key">
                        Motoring convictions:
                    </div>
                    {this.renderConvictionDetails(driver)}
                </div>
            )
        }
    }

    private renderConvictionDetails(driver: any): React.ReactNode[] | React.ReactNode {
        if (driver.hasConvictions && driver.convictions?.length > 0) {
            return driver.convictions.map((conviction: any) => (
                <div key={`${conviction.convictionCode}-${conviction.convictionDate}`} className="detail-value">
                    {`${conviction.convictionCode}, ${formatDate(conviction.convictionDate, "MMM YYYY")}`}&nbsp;
                </div>
            ))
        } else {
            return <div className="detail-value">None</div>
        }
    }

    private renderCancelPolicyButton(index: number): React.ReactNode {
        const hasPendingRenewal: boolean = this.props.renewalProposal != null && this.props.renewalProposal.status === POLICY_STATUS_PENDING_RENEWAL;
        if (hasPendingRenewal) {
            return (
                <ButtonLink
                    key={index}
                    id={`cancel-policy${index}`}
                    className="cancel-policy"
                    primary={false}
                    to={`/dashboard/annualMotor/cancel/${this.policyToRender.policyNumber}`}
                    routeState={{
                        isRenewalCancellation: true,
                        renewalCancellationUrl: '/journey/init/product/AnnualMotorInsurance/process/renewal/renewalMTCEntryPage/' + this.props.renewalProposal!._id
                    }}
                    disabled={this.props.subscription ? this.props.subscription.allAnnualMotorPoliciesAreCancelled : false}
                >
                    Cancel policy
                </ButtonLink>
            )
        } else {
            return (
                <ButtonLink
                    key={index}
                    id={`cancel-policy${index}`}
                    className="cancel-policy"
                    primary={false}
                    to={`/journey/init/product/AnnualMotorInsurance/process/mtc/presetPage/${this.policyToRender.interaction?._id}`}
                    disabled={this.props.subscription ? this.props.subscription.allAnnualMotorPoliciesAreCancelled : false}
                >
                    Cancel policy
                </ButtonLink>
            )
        }
    }

    private renderCoverTierHeader(coverTier: string): React.ReactNode {
        const HEADER_BY_TIER: Record<string, React.ReactNode> = {
            [MOTOR_COVER_TIERS.MOTOR_TIER_1]: <>Britannia<br/>Essentials</>,
            [MOTOR_COVER_TIERS.MOTOR_TIER_2]: <>Britannia</>,
            [MOTOR_COVER_TIERS.MOTOR_TIER_3]: <>Britannia<br/>Premier</>
        };

        const content = HEADER_BY_TIER[coverTier];
        if (!content) {
            throw new Error(`Unknown cover tier: ${coverTier}`);
        }

        return (
            <div className="h3 text-center policy-heading bn-policy-heading margin-top-0 cover-tier-header">
                {content}
            </div>
        );
    }

    private getDetail(description: string, value: string | null): React.ReactElement {
        return (
            <div className="detail">
                <div className="detail-key">{description}</div>
                {value}
            </div>
        )
    }

    private getGHCIncludeWording(coverTier: string, optionalExtras: any): string {
        switch (coverTier) {
            case MOTOR_COVER_TIERS.MOTOR_TIER_2:
                return optionalExtras.courtesyCarTotalLoss ? "Included" : "Not included";
            case MOTOR_COVER_TIERS.MOTOR_TIER_1:
                return optionalExtras.courtesyCar ? "Included" : "Not included";
            default:
                return "Included as standard";
        }
    }

    private getGHCIncludeDescription(coverTier: string): string {
        if (coverTier === MOTOR_COVER_TIERS.MOTOR_TIER_2) {
            return "Guaranteed hire car if your car’s stolen or written off:";
        } else {
            return "Guaranteed hire car if your car’s stolen, written off or being repaired:";
        }
    }

    private fetchLookupValues(lookupName: string) {
        this.lookupService.fetchLookupValues(lookupName, "en-UK")
            .subscribe((lookupValues: ListItem[]) => {
                this.setState((previousState) => ({
                    lookup: {
                        ...previousState.lookup, [lookupName]: lookupValues
                    }
                }))
            }, () => {
                throw new Error(`Error: failed to fetch lookup values for '${lookupName}' lookup`);
            });
    }

    private resolveLookupValue(lookupName: string, lookupId: string): string {
        if (!lookupId || !this.state.lookup?.[lookupName]) {
            return "";
        }
        const listItem: ListItem | undefined = this.state.lookup[lookupName].find((listItem: ListItem) => `${listItem.id}` === `${lookupId}`);
        if (!listItem) {
            throw new Error(`Error: no item with id '${lookupId}' found in lookup '${lookupName}'`);
        }
        return listItem.value;
    }

    private getCoverTierIcon(coverTier: string): string {
        switch (coverTier) {
            case MOTOR_COVER_TIERS.MOTOR_TIER_1:
                return 'BR-insurance-essentials.svg';
            case MOTOR_COVER_TIERS.MOTOR_TIER_2:
                return 'BR-insurance.svg';
            default:
                return 'BR-insurance-premier.svg';
        }
    }

    private ncdEarnTypeWording(id: number): string {
        const variants = new Map<number, string>([
            [1, `With ${this.vehicleToRender.make}, ${this.vehicleToRender.model} ${this.vehicleToRender.registrationNumber ? this.vehicleToRender.registrationNumber : ""} or a previous car`],
            [2, "With a company car"],
            [3, "Named driver on another policy"],
            [4, "In another country"]
        ]);
        return variants.get(Number(id)) as string;
    }
}
